"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.getVisitedPlaceDescription = exports.validateButtonActionWithCompleteSection = exports.validateButtonAction = exports.secondsSinceMidnightToTimeStrWithSuffix = exports.getFormattedDate = exports.getGenderedStrings = void 0;
const moment_1 = __importDefault(require("moment"));
const i18n_config_1 = __importDefault(require("../../config/i18n.config"));
const DateTimeUtils_1 = require("chaire-lib-common/lib/utils/DateTimeUtils");
const helpers_1 = require("evolution-common/lib/utils/helpers");
const genderedSuffixes = {};
/**
 * Get translated gendered strings for a person, that can be used as
 * placeholders in a localizable string in the translation files. For example, a
 * translation string could be "{{doesHeShe}} own a car" and the translation
 * would be "Does he own a car?" or "Does she own a car?" depending on the
 * person's specified gender
 *
 * Usage:
 * ```
 * ...widgetConfiguration,
 * label: (t: TFunction, interview) => t('survey:question', {
 *      ...getGenderedStrings(getActiverPerson(interview), t),
 *      otherContextData: 'myDate'
 * })
 * ```
 *
 * @param person The person to get the gendered strings for
 * @param t The translation function to translate the gender strings
 * @returns An object containing the gendered strings that can be added as an
 * option to a `t` function call to be replaced in the localized strings.
 */
const getGenderedStrings = (person, t) => {
    const gender = person !== null && person !== undefined && typeof person.gender === 'string' ? person.gender : 'none';
    if (genderedSuffixes[gender] === undefined) {
        genderedSuffixes[gender] = {};
    }
    const suffixes = genderedSuffixes[gender][i18n_config_1.default.language]
        ? genderedSuffixes[gender][i18n_config_1.default.language]
        : {
            suffixE: t('survey:suffixE', { context: gender }),
            suffixEurRice: t('survey:suffixEurRice', { context: gender }),
            suffixErEre: t('survey:suffixErEre', { context: gender }),
            isHeShe: t('survey:isHeShe', { context: gender }),
            doesHeShe: t('survey:doesHeShe', { context: gender }),
            heShe: t('survey:heShe', { context: gender }),
            ifHeShe: t('survey:ifHeShe', { context: gender }),
            himHerThem: t('survey:himHerThem', { context: gender }),
            hisHerTheir: t('survey:hisHerTheir', { context: gender })
        };
    if (genderedSuffixes[gender][i18n_config_1.default.language] === undefined) {
        genderedSuffixes[gender][i18n_config_1.default.language] = suffixes;
    }
    return suffixes;
};
exports.getGenderedStrings = getGenderedStrings;
/**
 * Get the formatted date in the requested locale
 * @param date The date string, formatted YYYY-MM-DD
 * @param options The options
 * @param {boolean} [options.withRelative=false] Whether to add a relative date (eg. yesterday, day before yesterday)
 * @param {string} [options.locale=undefined] The locale to use or undefined to use the default locale
 * @param {boolean} [options.withDayOfWeek=false] Whether to add the day of the week
 * @returns
 */
const getFormattedDate = (date, { withRelative = false, locale = undefined, withDayOfWeek = false } = {}) => {
    const tripsDate = (0, moment_1.default)(date);
    const format = withDayOfWeek ? 'dddd LL' : 'LL';
    let formattedTripsDate = locale ? tripsDate.locale(locale).format(format) : tripsDate.format(format);
    if (withRelative) {
        const today = (0, moment_1.default)(0, 'HH'); // today
        const numberOfDaysDiff = today.diff(tripsDate, 'days');
        if (numberOfDaysDiff >= 0) {
            formattedTripsDate += ` (${i18n_config_1.default.t('survey:pastToday', { count: numberOfDaysDiff })})`;
        }
        else {
            formattedTripsDate += ` (${i18n_config_1.default.t('survey:futureToday', { count: Math.abs(numberOfDaysDiff) })})`;
        }
    }
    return formattedTripsDate;
};
exports.getFormattedDate = getFormattedDate;
/**
 * Get the human readable time from the seconds since midnight. If the time is
 * the next day, it appends a suffix to the time.
 *
 * @param {number} secondsSinceMidnight The number of seconds since midnight to
 * convert to a time string
 * @param {string} [suffixAfterMidnight='the next day'] The suffix to append to
 * the time if it is the next
 * @returns A human readable time string
 */
const secondsSinceMidnightToTimeStrWithSuffix = function (secondsSinceMidnight, suffixAfterMidnight = i18n_config_1.default.t('main:theNextDay')) {
    if (secondsSinceMidnight >= 24 * 3600) {
        secondsSinceMidnight -= 24 * 3600;
        return `${(0, DateTimeUtils_1.secondsSinceMidnightToTimeStr)(secondsSinceMidnight)} ${suffixAfterMidnight}`;
    }
    else {
        return `${(0, DateTimeUtils_1.secondsSinceMidnightToTimeStr)(secondsSinceMidnight)}`;
    }
};
exports.secondsSinceMidnightToTimeStrWithSuffix = secondsSinceMidnightToTimeStrWithSuffix;
const validateButtonAction = (callbacks, interview, path, section, _sections, saveCallback) => {
    callbacks.startUpdateInterview({ sectionShortname: section, valuesByPath: { _all: true }, interview }, (updatedInterview) => {
        if (updatedInterview.allWidgetsValid) {
            if (typeof saveCallback === 'function') {
                saveCallback(callbacks, updatedInterview, path);
            }
            else {
                // scroll to top of the page
                window.scrollTo(0, 0);
                callbacks.startNavigate();
            }
        }
    });
};
exports.validateButtonAction = validateButtonAction;
// Add that the section is completed when the button is clicked in addition to navigating to next section
// FIXME This is a temporary move to typescript, but section navigation should be handled server side
const validateButtonActionWithCompleteSection = (callbacks, _interview, path, _section, sections, saveCallback) => {
    callbacks.startNavigate(undefined, (updatedInterview, targetSection) => {
        if (updatedInterview.allWidgetsValid) {
            if (typeof saveCallback === 'function') {
                saveCallback(callbacks, updatedInterview, path);
            }
            else {
                // Calculate the survey completion percentage based on the number of completed sections
                // FIXME Completion rate should be calculated by the navigation service itself
                const completionPercentage = (0, helpers_1.calculateSurveyCompletionPercentage)({
                    interview: updatedInterview,
                    sections,
                    sectionName: targetSection.sectionShortname,
                    sectionTarget: 'nextSection'
                });
                // Update the completion percentage and scroll to top of page
                window.scrollTo(0, 0);
                callbacks.startUpdateInterview({
                    valuesByPath: {
                        'response._completionPercentage': completionPercentage
                    }
                });
            }
        }
    });
};
exports.validateButtonActionWithCompleteSection = validateButtonActionWithCompleteSection;
/**
 * Get the visited place description
 * @param visitedPlace The visited place for which to get the description
 * @param withTimes Whether to add the times to the description
 * @param allowHtml Whether the description can contain HTML characters
 * @returns
 */
const getVisitedPlaceDescription = function (visitedPlace, withTimes = false, allowHtml = true) {
    let times = '';
    if (withTimes) {
        const arrivalTime = visitedPlace.arrivalTime !== undefined ? ' ' + (0, DateTimeUtils_1.secondsSinceMidnightToTimeStr)(visitedPlace.arrivalTime) : '';
        const departureTime = visitedPlace.departureTime !== undefined
            ? ' -> ' + (0, DateTimeUtils_1.secondsSinceMidnightToTimeStr)(visitedPlace.departureTime)
            : '';
        times = arrivalTime + departureTime;
    }
    if (allowHtml) {
        return `${i18n_config_1.default.t(`survey:visitedPlace:activities:${visitedPlace.activity}`)}${visitedPlace.name ? ` • <em>${visitedPlace.name}</em>` : ''}${times}`;
    }
    else {
        return `${i18n_config_1.default.t(`survey:visitedPlace:activities:${visitedPlace.activity}`)}${visitedPlace.name ? ` • ${visitedPlace.name}` : ''}${times}`;
    }
};
exports.getVisitedPlaceDescription = getVisitedPlaceDescription;
//# sourceMappingURL=frontendHelper.js.map